/**
 * Step 8: Create all remaining sections for each tunnel (fans already created in step 4)
 *
 * Refactored Flow:
 * For each tunnel:
 * 1. Get valid sections
 * 2. Rate all sections FIRST (filters, coils) - get model data
 * 3. Check for errors - throw if any section failed
 * 4. PHASE 1: Create all non-fan sections
 * 5. PHASE 2: Create all components (filters, coils) in sections
 * 6. PHASE 3: Update all section lengths
 * 7. PHASE 4: Create all accessories (access doors, openings)
 */
import {
  IBotData,
  IBotSection,
  IAHUParams,
  ICoilParams,
  IIdNameValue,
} from '../../../../models';
import { DEFAULT_COEFFICIENTS, SectionCreationContext } from '../../../../models/bot';
import { processSectionsForModelData } from '../../designSections/sections';
import {
  calculateExtraLengthForFan,
  createNotCreatedSection,
  calculateLengthAdjustments,
  createSectionComponent,
  updateSectionLength,
} from '../../designSections/sectionCreation';

export async function step8CreateRemainingSections(
  botData: IBotData,
  tunnelIds: number[],
  _tunnels: any[], // Not used in this step (fans already created in step 4)
  sizerSettings: any,
  thickness: number | null,
  casingInsulationId: number | null,
  casingInnerlinerId: number | null,
  floorInnerlinerId: number | null,
  ahuParams: IAHUParams,
  coilParams: ICoilParams,
  connHands: IIdNameValue[],
  ahuParamsResult: any,
  getOptions: () => { signal?: AbortSignal },
  onProgress?: (coefficient: number) => void,
  checkStopped?: () => boolean,
): Promise<{
  createdSections: number[];
  sectionErrors: Array<{
    sectionId: string;
    sectionType: string;
    error: string;
  }>;
}> {
  const createdSections: number[] = [];
  const sectionErrors: Array<{
    sectionId: string;
    sectionType: string;
    error: string;
  }> = [];

  // Process each tunnel sequentially
  for (
    let tunnelIndex = 0;
    tunnelIndex < botData.tunnels.length;
    tunnelIndex++
  ) {
    if (checkStopped?.()) {
      break;
    }

    const botTunnel = botData.tunnels[tunnelIndex];
    const currentTunnelId = tunnelIds[tunnelIndex];

    // ========================================================================
    // STEP 1: Get valid sections for this tunnel
    // ========================================================================
    const validSections = botTunnel.sections.filter((s) => s.type);

    if (validSections.length === 0) {
      continue;
    }

    // ========================================================================
    // STEP 2: Rate all sections FIRST (matches old Backbone getSections)
    // ========================================================================
    const sectionsWithModelData = await processSectionsForModelData(
      validSections,
      ahuParamsResult,
      botData,
      ahuParams,
      coilParams,
      connHands,
      sizerSettings,
      tunnelIndex,
      getOptions,
      onProgress,
    );

    // ========================================================================
    // STEP 4: Check for errors and stop immediately if any section has an error
    // ========================================================================
    // Find the first section with an error
    const errorSection = sectionsWithModelData.find((s) => s.modelData?.error);

    if (errorSection && errorSection.modelData?.error) {
      // Store error on botData section for UI display
      const botDataSectionIndex = botTunnel.sections.findIndex(
        (s) => s.id === errorSection.section.id,
      );
      if (botDataSectionIndex !== -1) {
        botTunnel.sections.forEach((section, index) => {
          if (index === botDataSectionIndex)
            section = {
              ...section,
              error: errorSection.modelData.error!,
            };
        });
      }

      // Return immediately with error information
      sectionErrors.push({
        sectionId: errorSection.section.id,
        sectionType: errorSection.section.type || 'unknown',
        error: errorSection.modelData.error,
      });

      return { createdSections, sectionErrors };
    }

    // ========================================================================
    // STEP 5: Filter to non-fan sections (fan already created above)
    // ========================================================================
    const nonCreatedSections = sectionsWithModelData.filter(
      (s) =>
        s.section.type &&
        s.section.type !== 'er_wheel' &&
        s.section.type !== 'fan',
    );

    // Calculate total sections length (including potential plenum)
    let validSectionsLength = nonCreatedSections.length;

    // ========================================================================
    // STEP 6: Create section creation context
    // ========================================================================
    const sectionContext: SectionCreationContext = {
      tunnelId: currentTunnelId,
      sizerSettings,
      thickness,
      casingInsulationId,
      casingInnerlinerId,
      floorInnerlinerId,
      validSectionsLength,
      getOptions,
      onProgress: (coeff: number) => onProgress?.(coeff),
      checkStopped: checkStopped || (() => false),
    };

    // ========================================================================
    // PHASE 1: Create all non-fan sections first
    // ========================================================================
    interface SectionCreationData {
      botSection: IBotSection;
      modelData: any;
      resp: any;
      initialLength: number;
      sectionTag: string;
      drainPan: any;
      adjustedLength: number;
      orderRate: number;
      nextSection: IBotSection | null;
      isLastSection: boolean;
    }

    const sectionCreationData: SectionCreationData[] = [];

    // Helper function to get the correct order_rate for database (absolute position including fan)
    // and sequential orderRate for length calculations (relative to non-fan sections only)
    const getOrderRatesForNonCreatedSection = (
      nonCreatedSectionIndex: number,
    ): {
      dbOrderRate: number; // For database - absolute position in validSections
      lengthOrderRate: number; // For length calculations - sequential for non-fan sections only
    } => {
      // Find the original index of this non-fan section in validSections
      const nonCreatedSection = nonCreatedSections[nonCreatedSectionIndex];
      const originalIndex = validSections.findIndex(
        (s) => s.id === nonCreatedSection.section.id,
      );

      const dbOrderRate =
        originalIndex !== -1 ? originalIndex + 1 : nonCreatedSectionIndex + 1;
      // For length calculations, use sequential numbering for non-fan sections (1, 2, 3...)
      const lengthOrderRate = nonCreatedSectionIndex + 1;

      return { dbOrderRate, lengthOrderRate };
    };

    for (
      let sectionIndex = 0;
      sectionIndex < nonCreatedSections.length;
      sectionIndex++
    ) {
      if (checkStopped?.()) {
        break;
      }
      const { section: botSection, modelData } =
        nonCreatedSections[sectionIndex];

      // Get correct order_rates for this section
      // dbOrderRate: absolute position including fan (for database)
      // lengthOrderRate: sequential for non-fan sections only (for length calculations)
      const { dbOrderRate, lengthOrderRate } =
        getOrderRatesForNonCreatedSection(sectionIndex);
      const nextSection = validSections[dbOrderRate] || null;
      // Calculate extra length needed for fan clearances
      const extraLength = calculateExtraLengthForFan(
        botSection,
        nextSection,
        dbOrderRate,
        thickness,
      );

      // Create the section with dbOrderRate (absolute position)
      const {
        resp,
        length: initialLength,
        sectionTag,
        drainPan,
      } = await createNotCreatedSection(
        botSection,
        modelData,
        sectionContext,
        dbOrderRate,
        extraLength,
        nextSection,
        floorInnerlinerId,
        casingInnerlinerId,
      );

      if (resp) {
        createdSections.push(resp.id);
        botSection.model = resp;
      }

      // Calculate adjusted length based on position and settings
      // Use lengthOrderRate (sequential for non-fan sections) for length calculations
      const adjustedLength = calculateLengthAdjustments(
        lengthOrderRate,
        validSectionsLength,
        botSection,
        initialLength,
        sizerSettings,
        thickness,
      );
      // Determine if this is the last section (check using lengthOrderRate)
      const expectedLastSectionOrderRate = validSectionsLength;
      const isLastSection = lengthOrderRate === expectedLastSectionOrderRate;

      // Store section creation data for later phases
      // Use dbOrderRate (absolute position) for stored orderRate
      sectionCreationData.push({
        botSection,
        modelData,
        resp,
        initialLength,
        sectionTag,
        drainPan,
        adjustedLength,
        orderRate: dbOrderRate,
        nextSection,
        isLastSection,
      });
    }
    // ========================================================================
    // Update fan and rotary htex section order_rate to its correct position
    // ========================================================================
    // await Promise.all(
    //   botData.tunnels[tunnelIndex].sections.map(async (section, idx) => {
    //     if (
    //       (section.type !== 'fan' && section.type !== 'er_wheel') ||
    //       section.model?.order_rate === idx + 1
    //     ) {
    //       return;
    //     }

    //     section.model = await dispatchMutation(
    //       ahuApi.endpoints.moveSection,
    //       {
    //         id: section.model?.id,
    //         body: {
    //           next_to: idx + 1,
    //         },
    //       },
    //       getOptions(),
    //     );
    //   }),
    // );

    // ========================================================================
    // PHASE 2: Create all components (filters, coils) for all sections
    // ========================================================================
    for (const sectionData of sectionCreationData) {
      if (checkStopped?.()) {
        break;
      }

      await createSectionComponent(
        sectionData.botSection,
        sectionData.resp.id,
        sectionContext,
        sectionData.modelData,
      );
    }

    // ========================================================================
    // PHASE 3: Update all section lengths
    // ========================================================================
    const updatedSections: Map<number, any> = new Map();

    for (const sectionData of sectionCreationData) {
      if (checkStopped?.()) {
        break;
      }

      const updatedSec = await updateSectionLength(
        sectionData.resp.id,
        sectionData.botSection,
        sectionData.adjustedLength,
        sectionData.sectionTag,
        sectionData.modelData,
        sectionContext,
        sectionData.nextSection,
        sectionData.drainPan,
      );

      const finalSection = updatedSec || sectionData.resp;
      updatedSections.set(sectionData.resp.id, finalSection);
      sectionData.botSection.model = finalSection;
      onProgress?.(DEFAULT_COEFFICIENTS.update_section); // update_section coefficient
    }

    // Note: Accessories (opening, access_door) are now created in step 9
  }

  return { createdSections, sectionErrors };
}
